import React, { useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Recruiter {
  id: string;
  name: string;
  email: string;
}

interface RecruiterMultiSelectorProps {
  recruiters: Recruiter[];
  value: string[];
  onChange: (recruiterIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const RecruiterMultiSelector: React.FC<RecruiterMultiSelectorProps> = ({
  recruiters,
  value,
  onChange,
  placeholder = "Search and select recruiters...",
  disabled = false,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredRecruiters = recruiters.filter(recruiter =>
    recruiter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recruiter.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedRecruiters = recruiters.filter(recruiter => value.includes(recruiter.id));

  const handleToggle = (recruiterId: string) => {
    if (value.includes(recruiterId)) {
      onChange(value.filter(id => id !== recruiterId));
    } else {
      onChange([...value, recruiterId]);
    }
  };

  const handleRemove = (recruiterId: string) => {
    onChange(value.filter(id => id !== recruiterId));
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredRecruiters.map(r => r.id);
    const allFilteredSelected = filteredIds.every(id => value.includes(id));
    
    if (allFilteredSelected) {
      // Deselect all filtered
      onChange(value.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered (merge with existing selections)
      const newSelections = [...value];
      filteredIds.forEach(id => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      onChange(newSelections);
    }
  };

  const allFilteredSelected = filteredRecruiters.length > 0 && 
    filteredRecruiters.every(recruiter => value.includes(recruiter.id));

  return (
    <div className="space-y-2">
      {/* Selected Recruiters Chips */}
      {selectedRecruiters.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border border-glass-border/40 rounded-xl bg-glass-surface/30">
          {selectedRecruiters.map((recruiter) => (
            <div
              key={recruiter.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-500/20 border border-primary-500/30 rounded-lg text-sm"
            >
              <span className="text-text-enterprise-primary font-medium">
                {recruiter.name}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(recruiter.id)}
                  className="hover:bg-primary-500/30 rounded p-0.5 transition-colors"
                  title="Remove"
                >
                  <X className="w-3 h-3 text-text-enterprise-primary" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Search Input and Select All */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary w-4 h-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
              className={cn(
                "w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border",
                "bg-glass-surface/50 backdrop-blur-sm",
                "text-text-enterprise-primary placeholder-text-enterprise-tertiary",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                "transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                error ? "border-red-500/50" : "border-glass-border/40"
              )}
            />
          </div>
          {filteredRecruiters.length > 0 && (
            <button
              type="button"
              onClick={handleSelectAllFiltered}
              disabled={disabled}
              className={cn(
                "text-xs font-medium px-3 py-2 rounded-lg transition-colors",
                "text-primary-500 hover:text-primary-600 hover:bg-primary-500/10",
                "disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              )}
            >
              {allFilteredSelected ? 'Deselect All' : `Select All (${filteredRecruiters.length})`}
            </button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-xs text-red-500">{error}</p>
        )}

        {/* Recruiter List */}
        <div className="border border-glass-border/40 rounded-xl overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {filteredRecruiters.length === 0 ? (
              <div className="p-6 text-center text-text-enterprise-tertiary">
                <p className="text-sm">
                  {searchTerm ? 'No recruiters found matching your search.' : 'No recruiters available.'}
                </p>
              </div>
            ) : (
              <div className="py-1">
                {filteredRecruiters.map((recruiter) => {
                  const isSelected = value.includes(recruiter.id);
                  return (
                    <label
                      key={recruiter.id}
                      className={cn(
                        "flex items-center gap-3 p-3 cursor-pointer transition-colors",
                        "hover:bg-glass-surface/30",
                        isSelected && "bg-primary-500/10"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(recruiter.id)}
                        disabled={disabled}
                        className="w-4 h-4 text-primary-600 bg-glass-surface border-glass-border/50 rounded focus:ring-primary-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-text-enterprise-primary">
                          {recruiter.name}
                        </div>
                        <div className="text-xs text-text-enterprise-tertiary">
                          {recruiter.email}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Selection Count */}
        {value.length > 0 && (
          <div className="text-xs text-text-enterprise-tertiary text-center">
            {value.length} recruiter{value.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>
    </div>
  );
};

